import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { User, Storefront, MagnifyingGlass, CalendarBlank, Lightning, Key, Copy, X, CaretDown } from "@phosphor-icons/react";
import ScheduleExtractor from "../components/admin/ScheduleExtractor";
import AppInstallsTile from "../components/admin/AppInstallsTile";
import EmailLogCard from "../components/admin/EmailLogCard";
import EmailSuppressionsCard from "../components/admin/EmailSuppressionsCard";
import HotspotConfigCard from "../components/admin/HotspotConfigCard";
import CleanupCuisineTagsCard from "../components/admin/CleanupCuisineTagsCard";
import TrafficSourcesCard from "../components/admin/TrafficSourcesCard";
import AuditImprecisePinsCard from "../components/admin/AuditImprecisePinsCard";
import ResendDomainHealthCard from "../components/admin/ResendDomainHealthCard";
import FollowAnnouncementCard from "../components/admin/FollowAnnouncementCard";
import GraceEngagementCard from "../components/admin/GraceEngagementCard";
import ThisWeekEventsCard from "../components/admin/ThisWeekEventsCard";
import AdminMarketplaceAnalyticsCard from "../components/admin/AdminMarketplaceAnalyticsCard";
import AdminFeatureFlagsCard from "../components/admin/AdminFeatureFlagsCard";
import AdminBroadcastCard from "../components/admin/AdminBroadcastCard";
import InviteOwnerCard from "../components/admin/InviteOwnerCard";
import SignupTrendTile from "../components/admin/SignupTrendTile";
import UniqueVisitorsCard from "../components/admin/UniqueVisitorsCard";
import WeeklyVisitorsCard from "../components/admin/WeeklyVisitorsCard";
import RetentionTile from "../components/admin/RetentionTile";
import { CalendarConnectionsTile, PaidProTile, NeverPostedTile } from "../components/admin/IntegrationKpiTiles";
import CollapsibleSection from "../components/admin/CollapsibleSection";
import EmailDispatchCenter from "../components/admin/EmailDispatchCenter";
import CronHealthCard from "../components/admin/CronHealthCard";
import EmailConversionPanel from "../components/admin/EmailConversionPanel";
import AdminPushPanel from "../components/admin/AdminPushPanel";
import PushAnalyticsCard from "../components/admin/PushAnalyticsCard";
import SuspiciousTrucksCard from "../components/admin/SuspiciousTrucksCard";
import TestAccountCleanupCard from "../components/admin/TestAccountCleanupCard";
import OwnershipRequestsCard from "../components/admin/OwnershipRequestsCard";
import ProAccessRequestsCard from "../components/admin/ProAccessRequestsCard";
import AdminToolboxCard from "../components/admin/AdminToolboxCard";
import RegionLeaderboardCard from "../components/admin/RegionLeaderboardCard";
import AdminGraduationToaster from "../components/AdminGraduationToaster";
import BackfillLogoPinCard from "../components/admin/BackfillLogoPinCard";
import GcalDiagnoseCard from "../components/admin/GcalDiagnoseCard";
import AdminEventInquiriesCard from "../components/admin/AdminEventInquiriesCard";
import StripePricingDiagnostic from "../components/admin/StripePricingDiagnostic";
import MaintenanceTile from "../components/admin/MaintenanceTile";
import FoundingProReviewTile from "../components/admin/FoundingProReviewTile";
import TopSearchesTile from "../components/admin/TopSearchesTile";
import CalendarSyncSubscribersTile from "../components/admin/CalendarSyncSubscribersTile";
import AnnouncementTile from "../components/admin/AnnouncementTile";
import {
  Truck,
  Megaphone,
  MapPin,
  Tag,
  ImageSquare,
  Broom,
  Warning,
  ShieldStar,
  HandWaving,
  Image as ImageIcon,
  ChartLineUp,
  ToggleLeft,
  BellRinging,
  Stethoscope,
  ChatCircleText,
  PaperPlaneTilt,
  Crown,
  Calendar,
  CloudArrowUp,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtTime(raw) {
  if (!raw) return "—";
  if (/^\d{2}:\d{2}$/.test(raw)) return raw;
  try {
    return new Date(raw).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return raw;
  }
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Admin password-reset: tracks which user is currently being reset, and
  // holds the one-time temp password returned from the backend so the admin
  // can copy it before dismissing the modal.
  const [resettingId, setResettingId] = useState(null);
  const [resetResult, setResetResult] = useState(null); // { email, name, temp_password }

  const onResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? They'll need the new temp password to log in.`)) return;
    setResettingId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reset password");
    } finally {
      setResettingId(null);
    }
  };

  const onTogglePro = async (u) => {
    try {
      const { data } = await api.put(`/admin/users/${u.id}/pro`, { is_pro: !u.is_pro });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_pro: data.is_pro } : x)));
      toast.success(`${u.email} → Pro ${data.is_pro ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update Pro status");
    }
  };

  const onToggleProLabTester = async (u) => {
    try {
      const { data } = await api.put(
        `/admin/users/${u.id}/pro-lab-tester`,
        { pro_lab_tester: !u.pro_lab_tester });
      setUsers((prev) => prev.map(
        (x) => (x.id === u.id ? { ...x, pro_lab_tester: data.pro_lab_tester } : x)));
      toast.success(
        `${u.email} → Pro Lab ${data.pro_lab_tester ? "TESTER ON" : "TESTER OFF"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update Pro Lab tester");
    }
  };

  const copyTemp = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  const [q, setQ] = useState("");
  // Bumped every time the admin sends an invite, so the
  // ScheduleExtractor's truck dropdown re-fetches `/admin/trucks` and the
  // newly invited (pending) owner shows up immediately without a page
  // reload. Keeps the user's "invite then schedule" flow zero-friction.
  const [trucksRefreshTick, setTrucksRefreshTick] = useState(0);

  const reload = () => {
    if (!user?.is_admin) return;
    setLoading(true);
    Promise.all([api.get("/admin/users"), api.get("/admin/events")])
      .then(([u, e]) => {
        setUsers(u.data);
        setEvents(e.data);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Account-type filter for All Accounts section (Trucks/Customers).
  // Real user request May 2026: "add two buttons - Trucks and
  // Customers, admin must click on button to show list of each
  // category sorted by date added with most recent on top".
  const [accountFilter, setAccountFilter] = useState("trucks");

  const filteredUsers = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = users.filter((u) =>
      accountFilter === "trucks" ? u.role === "owner" : u.role !== "owner"
    );
    const filtered = !s ? base : base.filter(
      (u) =>
        u.email?.toLowerCase().includes(s) ||
        u.name?.toLowerCase().includes(s) ||
        u.role?.toLowerCase().includes(s) ||
        // Match truck names too — so click-to-filter on a truck name
        // keeps its owner visible in the All Accounts table instead of
        // hiding them.
        (u.truck_names || []).some((tn) => tn.toLowerCase().includes(s)),
    );
    // Sort by date added desc (most recent first). Falls back to a
    // stable order for rows with no created_at so admins don't see
    // them shuffle on refresh.
    return [...filtered].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return (a.email || "").localeCompare(b.email || "");
    });
  }, [users, q, accountFilter]);

  const filteredEvents = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return events;
    return events.filter(
      (e) =>
        e.truck_name?.toLowerCase().includes(s) ||
        e.owner_email?.toLowerCase().includes(s) ||
        e.owner_name?.toLowerCase().includes(s) ||
        e.event_name?.toLowerCase().includes(s) ||
        e.address?.toLowerCase().includes(s),
    );
  }, [events, q]);

  const ownerCount = users.filter((u) => u.role === "owner").length;
  const customerCount = users.filter((u) => u.role === "customer").length;
  // 24h split fallbacks for the two sparkline tiles. Used only when
  // /admin/signup-trend errors — otherwise the tiles pull straight from
  // the server's per-day owners/customers buckets.
  const { newOwners24h, newCustomers24h } = useMemo(() => {
    // Compute the rolling-24h cutoff outside the loop. Re-runs on every
    // users[] change, which is the right cadence.
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    let o = 0, c = 0;
    users.forEach((u) => {
      if (!u.created_at) return;
      const t = new Date(u.created_at).getTime();
      if (!Number.isFinite(t) || t < cutoff) return;
      if (u.role === "owner") o++; else c++;
    });
    return { newOwners24h: o, newCustomers24h: c };
  }, [users]);

  // Collapsible sections — keep the long lists out of the way until
  // the admin actively wants them. Users defaults open showing the 10
  // most-recent rows; Events is collapsed entirely.
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const visibleUsers = usersExpanded ? filteredUsers : filteredUsers.slice(0, 10);
  const hiddenUserCount = Math.max(0, filteredUsers.length - visibleUsers.length);

  // Click-to-filter from a truck-name chip on an owner row. Sets the
  // shared `q` filter (which the events table already keys off), expands
  // the events section so the rows are actually visible, and scrolls
  // to it on the next frame so the admin lands on the filtered view.
  const onTruckClick = (truckName) => {
    setQ(truckName);
    setEventsExpanded(true);
    requestAnimationFrame(() => {
      document
        .querySelector('[data-testid="admin-events-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  if (authLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin" }} />;
  if (!user.is_admin)
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="admin-forbidden">
        <h2 className="font-display font-bold text-2xl">Not authorized</h2>
        <p className="text-sm text-muted-foreground mt-2">
          This area is restricted to the app owner.
        </p>
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-testid="admin-page">
      <AdminGraduationToaster />
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">Admin</span>
          <h1 className="mt-1 font-display font-bold text-3xl sm:text-4xl tracking-tight">Reporting</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {users.length} account{users.length === 1 ? "" : "s"} · {events.length} event
            {events.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlass weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, email, truck, address…"
            className="pl-9 w-full sm:w-80"
            data-testid="admin-search"
          />
        </div>
      </header>

      {/* Top-of-page health banner — surfaces Resend sending-domain
          status. Hidden until the first hourly health check runs.
          Real bug report 2026-05: the 6AM Admin Daily Digest failed
          silently for 3 days before being noticed. Never again. */}
      <ResendDomainHealthCard />

      {/* Admin-only preview affordance — surfaces the gated profile-editor
          and v1 profile-preview tools. Hidden from non-admin users by the
          page's own auth guard. */}
      <div className="flex flex-wrap gap-2 -mt-3" data-testid="admin-preview-links">
        <Link
          to="/admin/profile-editor"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-primary/10 hover:bg-primary/15 text-primary text-xs font-bold border border-primary/30 transition"
          data-testid="admin-link-profile-editor"
        >
          🧪 Profile Editor (preview)
        </Link>
        <span className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-muted text-xs font-medium text-muted-foreground border border-border">
          Hidden from customers · admin-only
        </span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3" data-testid="admin-kpi-row">
        {[
          { label: "Owners", value: ownerCount, icon: Storefront },
          { label: "Customers", value: customerCount, icon: User },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <k.icon weight="duotone" className="h-4 w-4 text-primary" />
            </div>
            <div className="mt-2 font-display font-bold text-2xl">{k.value}</div>
          </div>
        ))}
        <SignupTrendTile kind="owners" fallbackToday={newOwners24h} />
        <SignupTrendTile kind="customers" fallbackToday={newCustomers24h} />
        <UniqueVisitorsCard />
        <WeeklyVisitorsCard />
        <RetentionTile />
        <PaidProTile />
        <CalendarConnectionsTile />
        <NeverPostedTile />
        {[
          { label: "One-off posts", value: events.filter((e) => e.source === "daily").length, icon: Lightning },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <k.icon weight="duotone" className="h-4 w-4 text-primary" />
            </div>
            <div className="mt-2 font-display font-bold text-2xl">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Admin-only: live app-install proxy (push registrations) — instant
          numbers while Apple's App Analytics lags 24-48h. */}
      <AppInstallsTile />

      {/* Admin-only: this calendar week's coverage — owners + trucks + total events with daily distribution */}
      <ThisWeekEventsCard />

      {/* Admin-only: marketplace intelligence — Pro vs Regular cohort
          comparison across 9 engagement metrics + searchable single-truck
          drilldown with daily series + top engagement days. Collapsed by
          default — strategic but not day-1 critical. */}
      <div className="mt-6">
        <CollapsibleSection
          title="Marketplace intelligence"
          subtitle="Pro vs Regular cohort comparison, boost ROI, single-truck drilldown."
          icon={ChartLineUp}
          testId="collapsible-marketplace"
        >
          <AdminMarketplaceAnalyticsCard />
        </CollapsibleSection>
      </div>

      {/* Admin-only: feature flags master control. Collapsed by default. */}
      <div className="mt-6">
        <CollapsibleSection
          title="Feature flags"
          subtitle="Toggle every feature in subscription / visibility / boost / analytics / integrations / weather groups."
          icon={ToggleLeft}
          testId="collapsible-feature-flags"
        >
          <AdminFeatureFlagsCard />
        </CollapsibleSection>
      </div>

      {/* Admin-only: page_view breakdown by source channel (Google /
          Facebook / Email / Direct / etc). Collapsed by default —
          summary numbers visible from the KPI strip already, full
          channel breakdown lives behind the click. */}
      <CollapsibleSection
        title="Traffic sources"
        subtitle="Page views, unique sessions, channel breakdown, direct vs share."
        icon={ChartLineUp}
        testId="collapsible-traffic-sources"
      >
        <TrafficSourcesCard />
      </CollapsibleSection>

      {/* Truck help box (May 2026) — collapses 9 low-frequency admin
          tools into a single accordion so the dashboard scrolls less
          and loads faster. Each row is lazy-mounted on click so the
          components that fetch on render (e.g. ScheduleExtractor)
          don't fire until the admin opens them. */}
      <AdminToolboxCard
        tools={[
          {
            key: "invite-owner",
            label: "Add a truck",
            summary: "Pre-create an owner account + truck on someone else's behalf.",
            icon: Truck,
            render: () => (
              <InviteOwnerCard
                onSent={() => {
                  reload();
                  setTrucksRefreshTick((t) => t + 1);
                }}
              />
            ),
          },
          {
            key: "preloaded-trucks-link",
            label: "Preloaded trucks → dedicated page",
            summary: "City-expansion workspace has moved to its own page. Click to open the full management UI (CSV import, edit, archive, invites, city metadata).",
            icon: CloudArrowUp,
            render: () => (
              <div className="space-y-3" data-testid="preloaded-trucks-link-tile">
                <p className="text-sm text-muted-foreground">
                  The preloaded-trucks CSV importer now lives on its own admin page so we can
                  add more expansion-engine surfaces (Import History, Claim Requests, Activity Log)
                  without crowding the main Admin dashboard.
                </p>
                <Link to="/admin/preloaded-trucks">
                  <Button data-testid="open-preloaded-trucks-page-btn">
                    Open Preloaded Trucks
                  </Button>
                </Link>
              </div>
            ),
          },
          {
            key: "resources-link",
            label: "Food Truck Resources (provider directory)",
            summary: "Trusted-business directory for owners: categories, map, sponsorships, suggestion & partner queues. Admin-toggled Coming Soon / Live.",
            icon: CloudArrowUp,
            render: () => (
              <div className="space-y-3" data-testid="resources-link-tile">
                <p className="text-sm text-muted-foreground">
                  Manage the resource directory: Coming Soon/Live toggle, categories, providers
                  (logos, service areas, sponsorship levels), suggestion & partner-inquiry
                  queues, and per-provider analytics for sponsor reports.
                </p>
                <Link to="/admin/resources">
                  <Button data-testid="open-resources-admin-btn">
                    Open Resource Directory
                  </Button>
                </Link>
              </div>
            ),
          },
          {
            key: "pro-challenge-link",
            label: "4-Week Pro Challenge (reactivation campaign)",
            summary: "Invite inactive free owners to post 4 consecutive weeks of schedules and earn 30 days of Pro free. Eligibility preview, flag-gated invitations, weekly evaluation, reward automation, audit trail.",
            icon: CloudArrowUp,
            render: () => (
              <div className="space-y-3" data-testid="pro-challenge-link-tile">
                <p className="text-sm text-muted-foreground">
                  Full campaign control room: preview exactly who qualifies, configure the
                  inactivity threshold and deadlines, review the invitation email, and manage
                  every participant week-by-week. Campaign + live sending stay OFF until
                  explicitly enabled.
                </p>
                <Link to="/admin/pro-challenge">
                  <Button data-testid="open-pro-challenge-page-btn">
                    Open Pro Challenge
                  </Button>
                </Link>
              </div>
            ),
          },
          {
            key: "blog-manager-link",
            label: "Blog Manager (SEO content)",
            summary: "Admin-only blog CMS: write, publish, and manage SEO articles at /blog. Markdown editor, cover images, categories, local targeting, live-data CTA sections.",
            icon: CloudArrowUp,
            render: () => (
              <div className="space-y-3" data-testid="blog-manager-link-tile">
                <p className="text-sm text-muted-foreground">
                  Create and manage the public blog. Published posts are auto-added to the
                  XML sitemap with Article + Breadcrumb structured data. Drafts stay hidden.
                </p>
                <Link to="/admin/blog">
                  <Button data-testid="open-blog-manager-btn">
                    Open Blog Manager
                  </Button>
                </Link>
              </div>
            ),
          },
          {
            key: "magic-import-announcement",
            label: "Magic Import announcement email",
            summary: "One-time 'you asked, we listened' email to all owners introducing Magic Schedule Import. Idempotent; honors unsubscribes.",
            icon: CloudArrowUp,
            render: () => <AnnouncementTile />,
          },
          {
            key: "expansion-engine-link",
            label: "Expansion Engine (Discovery → Enrichment)",
            summary: "Staged workflow: upload a seed CSV of truck names, resolve each truck's OFFICIAL website/socials with live verification, then auto-flow resolved rows into contact enrichment. Cost estimates + 5-row test mode before every batch.",
            icon: CloudArrowUp,
            render: () => (
              <div className="space-y-3" data-testid="expansion-engine-link-tile">
                <p className="text-sm text-muted-foreground">
                  Six staged phases: Upload Seed → Run Discovery → Review Discovery →
                  Run Enrichment → Review Final → Export/Import. Discovery fetch-verifies
                  candidate websites live (dead domains rejected, socials cross-linked from
                  verified sites = High confidence). Hunter.io only runs on High-confidence
                  or admin-approved rows.
                </p>
                <Link to="/admin/expansion">
                  <Button data-testid="open-expansion-engine-page-btn">
                    Open Expansion Engine
                  </Button>
                </Link>
              </div>
            ),
          },
          {
            key: "research-enrichment-link",
            label: "Research & contact enrichment (LLM)",
            summary: "Upload a CSV of expansion trucks and enrich each row with a best-guess website, email, socials, phone, and recommended contact method. Download the enriched importer CSV + outreach CRM workbook.",
            icon: CloudArrowUp,
            render: () => (
              <div className="space-y-3" data-testid="research-enrichment-link-tile">
                <p className="text-sm text-muted-foreground">
                  Feeds a CSV through GPT-5.5 to produce starting-point research records for
                  outreach — recommended contact method, plausible socials, verification-source
                  URLs. Everything is inline-editable; download an enriched importer CSV or an
                  outreach CRM workbook (<code>.xlsx</code>).
                </p>
                <Link to="/admin/research-enrichment">
                  <Button data-testid="open-research-enrichment-page-btn">
                    Open Research Enrichment
                  </Button>
                </Link>
              </div>
            ),
          },
          {
            key: "follow-announcement",
            label: "One-time announcement",
            summary: "Batch-send a one-shot push/email announcement to all followers.",
            icon: Megaphone,
            render: () => <FollowAnnouncementCard />,
          },
          {
            key: "broadcast-announcement",
            label: "Send a broadcast email",
            summary: "Templates for reusable feature announcements + a custom-message composer (markdown, live preview) for one-off owner blasts.",
            icon: PaperPlaneTilt,
            render: () => <AdminBroadcastCard />,
          },
          {
            key: "audit-pins",
            label: "Audit precise pin",
            summary: "Scan every schedule for centroid pins / pins >10mi from typed address.",
            icon: MapPin,
            render: () => <AuditImprecisePinsCard />,
          },
          {
            key: "cleanup-cuisine",
            label: "Clean up cuisine tags",
            summary: "One-shot DB migration to normalise cuisine_tags across all trucks.",
            icon: Tag,
            render: () => <CleanupCuisineTagsCard />,
          },
          {
            key: "schedule-extractor",
            label: "Schedule image extractor",
            summary: "OCR a flyer image into a truck's weekly schedule.",
            icon: ImageSquare,
            render: () => <ScheduleExtractor refreshKey={trucksRefreshTick} />,
          },
          {
            key: "test-hygiene",
            label: "Test account hygiene",
            summary: "Physically remove leftover pytest/demo accounts + cascade rows.",
            icon: Broom,
            render: () => <TestAccountCleanupCard />,
          },
          {
            key: "suspicious-trucks",
            label: "Suspicious truck names",
            summary: "Flag test/spam-shaped truck names before push surfaces them publicly.",
            icon: Warning,
            render: () => <SuspiciousTrucksCard />,
          },
          {
            key: "pro-requests",
            label: "Pro access requests",
            summary: "Approve / reject pending Pro upgrade requests from owners.",
            icon: ShieldStar,
            render: () => <ProAccessRequestsCard />,
          },
          {
            key: "ownership-requests",
            label: "Ownership requests",
            summary: "Approve / reject claim-this-truck requests from non-owner users.",
            icon: HandWaving,
            render: () => <OwnershipRequestsCard />,
          },
          {
            key: "backfill-logo-pin",
            label: "Backfill logo map pins",
            summary: "One-shot: enable the map-pin logo for every Pro who uploaded a logo but didn't toggle it on.",
            icon: ImageIcon,
            render: () => <BackfillLogoPinCard />,
          },
          {
            key: "gcal-diagnose",
            label: "GCal sync diagnose",
            summary: "Snapshot what Google actually returns for an owner's calendar vs what TruckTrackr has stored.",
            icon: Stethoscope,
            render: () => <GcalDiagnoseCard users={users} />,
          },
          {
            key: "stripe-pricing",
            label: "Stripe pricing diagnostic",
            summary: "Live-fetch the Stripe price IDs to verify current/legacy/annual are wired correctly. Catches the $5-vs-$15 env-var swap before any customer hits it.",
            icon: ChartLineUp,
            render: () => <StripePricingDiagnostic />,
          },
          {
            key: "event-inquiries",
            label: "Event inquiry moderation",
            summary: "Review every catering inquiry, close abusive/spam ones (owners already received the email but the host's status page reflects the close).",
            icon: ChatCircleText,
            render: () => <AdminEventInquiriesCard />,
          },
          {
            key: "maintenance",
            label: "Maintenance · cleanup & blasts",
            summary: "One-click placeholder-draft cleanup and the profile-nudge email blast to truck owners with no published profile. Throttled, with live job status.",
            icon: PaperPlaneTilt,
            render: () => <MaintenanceTile />,
          },
          {
            key: "founding-pro-review",
            label: "Founding Pro Review",
            summary: "Comped Pros missing profile fields. Send the Aug 1 warning + 7-day reminder; revoke per truck after deadline. Paying Pros are excluded automatically.",
            icon: Crown,
            render: () => <FoundingProReviewTile />,
          },
          {
            key: "top-searches",
            label: "Top searches · customer demand",
            summary: "What customers search for in the navbar. Top queries, zero-result queries (discovery gaps), and which trucks customers reach via search.",
            icon: MagnifyingGlass,
            render: () => <TopSearchesTile />,
          },
          {
            key: "calendar-sync-subscribers",
            label: "Calendar sync subscribers",
            summary: "How many customers have subscribed their personal calendar to followed-truck events. Top trucks by reach — Free trucks high on this list are great Pro-upgrade targets.",
            icon: Calendar,
            render: () => <CalendarSyncSubscribersTile />,
          },
        ]}
      />

      {/* Admin-only: push notifications (broadcast + test + performance).
          Merged into a single collapsible to keep the page tidy — push is
          important but not day-1 dashboard real estate. */}
      <CollapsibleSection
        title="Push notifications"
        subtitle="Manual broadcast, send-to-self test, and Phase E performance + return-rate analytics."
        icon={BellRinging}
        testId="collapsible-push"
      >
        <div className="space-y-6">
          <AdminPushPanel />
          <PushAnalyticsCard />
        </div>
      </CollapsibleSection>

      {/* Admin-only: 2-hour grace window engagement (informs whether to keep/extend/shrink) */}
      <GraceEngagementCard />

      {/* Per-region expansion leaderboard — new trucks/owners/customers
          + Founding Pro progress for every market. Pulls expansion_cities
          collection so adding a new city is zero-code on this surface. */}
      <RegionLeaderboardCard />

      {/* Admin-only: recent transactional-email send log with resend */}
      <EmailLogCard />

      {/* Admin-only: global do-not-email list — manual unsubscribes +
          event-inquiry opt-outs (Mar 2026 unsubscribe system) */}
      <EmailSuppressionsCard />

      {/* Did the scheduled batches actually fire? (multi-pod production
          diagnosis — built after the missing Sunday reminder incident) */}
      <CronHealthCard />

      {/* Admin-only: SINGLE source-of-truth for every email TruckTrackr
          sends. Consolidates the previous Cron triggers, Launch campaigns,
          and Email previews into one dropdown with contextual action
          buttons per template. */}
      <EmailDispatchCenter />

      {/* Unified email conversion KPI — clicks → posts-within-N-hours
          across every owner-targeted lifecycle email surface */}
      <EmailConversionPanel />

      {/* Admin-only: Smart Event Hotspot tuning (min-trucks + blacklist coords) */}
      <HotspotConfigCard />

      {/* Users — last 10 by default; click to expand for the full list */}
      <section data-testid="admin-users-section">
        <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
          <h2 className="font-display font-semibold text-xl tracking-tight" data-testid="admin-users-heading">
            All accounts
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredUsers.length})
            </span>
          </h2>
          <div className="flex items-center gap-2">
            {/* Trucks / Customers filter — defaults to Trucks since
                that's the segment admins act on most. */}
            <div
              className="inline-flex rounded-full border border-border bg-muted/30 p-0.5"
              role="tablist"
              aria-label="Account type"
            >
              {[
                { key: "trucks", label: "Trucks", count: ownerCount },
                { key: "customers", label: "Customers", count: customerCount },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={accountFilter === opt.key}
                  onClick={() => {
                    setAccountFilter(opt.key);
                    setUsersExpanded(false);
                  }}
                  data-testid={`admin-accounts-filter-${opt.key}`}
                  className={
                    "h-7 px-3 rounded-full text-xs font-bold transition " +
                    (accountFilter === opt.key
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {opt.label}
                  <span className="ml-1 opacity-70">{opt.count}</span>
                </button>
              ))}
            </div>
            {filteredUsers.length > 10 && (
              <button
                type="button"
                onClick={() => setUsersExpanded((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                data-testid="admin-users-toggle"
              >
                {usersExpanded ? "Show last 10" : `Show all ${filteredUsers.length}`}
                <CaretDown weight="bold" className={"h-3 w-3 transition-transform " + (usersExpanded ? "rotate-180" : "")} />
              </button>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Last login</th>
                <th className="text-center px-4 py-3">Pro</th>
                <th className="text-center px-4 py-3" title="Pro Lab beta tester allowlist">Lab</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground text-center">Loading…</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground text-center">No matches.</td></tr>
              ) : (
                visibleUsers.map((u) => (
                  <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <div>{u.name || "—"}</div>
                      {u.role === "owner" && u.truck_names && u.truck_names.length > 0 && (
                        <div
                          className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-normal"
                          data-testid={`admin-user-trucks-${u.id}`}
                        >
                          <span className="text-muted-foreground">🚚</span>
                          {u.truck_names.map((tn, i) => (
                            <button
                              key={tn + i}
                              type="button"
                              onClick={() => onTruckClick(tn)}
                              className="text-muted-foreground hover:text-primary hover:underline underline-offset-2 transition-colors"
                              title={`Filter events to "${tn}"`}
                              data-testid={`admin-truck-chip-${u.id}-${i}`}
                            >
                              {tn}
                              {i < u.truck_names.length - 1 && (
                                <span className="ml-1.5 text-muted-foreground/50">·</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {u.role === "owner" && u.home_base && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-normal text-muted-foreground bg-muted rounded-full px-2 py-0.5"
                          data-testid={`admin-user-homebase-${u.id}`}
                        >
                          <span aria-hidden>📍</span>
                          {u.home_base}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        u.role === "owner" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                      }`}>
                        {u.role === "owner" ? <Storefront weight="duotone" className="h-3 w-3" /> : <User weight="duotone" className="h-3 w-3" />}
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.last_login_at)}</td>
                    <td className="px-4 py-3 text-center">
                      {u.role === "owner" ? (
                        <button
                          type="button"
                          onClick={() => onTogglePro(u)}
                          role="switch"
                          aria-checked={u.is_pro}
                          className={`inline-flex items-center justify-center h-6 w-11 rounded-full transition-colors ${
                            u.is_pro ? "bg-primary" : "bg-muted-foreground/30"
                          }`}
                          data-testid={`admin-pro-toggle-${u.id}`}
                          title={u.is_pro ? "Pro is ON — click to revoke" : "Pro is OFF — click to grant"}
                        >
                          <span
                            className={`block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              u.is_pro ? "translate-x-2.5" : "-translate-x-2.5"
                            }`}
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.role === "owner" ? (
                        <button
                          type="button"
                          onClick={() => onToggleProLabTester(u)}
                          role="switch"
                          aria-checked={u.pro_lab_tester}
                          className={`inline-flex items-center justify-center h-6 w-11 rounded-full transition-colors ${
                            u.pro_lab_tester ? "bg-amber-500" : "bg-muted-foreground/30"
                          }`}
                          data-testid={`admin-pro-lab-tester-toggle-${u.id}`}
                          title={u.pro_lab_tester
                            ? "Pro Lab tester — click to remove from allowlist"
                            : "Not a Pro Lab tester — click to add to allowlist"}
                        >
                          <span
                            className={`block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              u.pro_lab_tester ? "translate-x-2.5" : "-translate-x-2.5"
                            }`}
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onResetPassword(u)}
                        disabled={resettingId === u.id}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                        data-testid={`admin-reset-pwd-${u.id}`}
                      >
                        <Key weight="duotone" className="h-3.5 w-3.5" />
                        {resettingId === u.id ? "Resetting…" : "Reset password"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
              {!loading && hiddenUserCount > 0 && (
                <tr className="border-t border-border bg-muted/20">
                  <td colSpan={7} className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => setUsersExpanded(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80"
                      data-testid="admin-users-expand-row"
                    >
                      Show {hiddenUserCount} more {hiddenUserCount === 1 ? "account" : "accounts"}
                      <CaretDown weight="bold" className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Events — fully collapsed by default; click header to expand */}
      <section data-testid="admin-events-section">
        <button
          type="button"
          onClick={() => setEventsExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 mb-3 text-left group"
          data-testid="admin-events-toggle"
          aria-expanded={eventsExpanded}
        >
          <h2 className="font-display font-semibold text-xl tracking-tight" data-testid="admin-events-heading">
            All events &amp; schedule entries
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredEvents.length})
            </span>
          </h2>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:text-primary/80">
            {eventsExpanded ? "Hide" : "Show"}
            <CaretDown weight="bold" className={"h-3 w-3 transition-transform " + (eventsExpanded ? "rotate-180" : "")} />
          </span>
        </button>
        {eventsExpanded && (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
              <tr>
                <th className="text-left px-4 py-3">Truck</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Day</th>
                <th className="text-left px-4 py-3">Hours</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground text-center">Loading…</td></tr>
              ) : filteredEvents.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground text-center">No matches.</td></tr>
              ) : (
                filteredEvents.map((e) => (
                  <tr key={e.id} className="border-t border-border" data-testid={`admin-event-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{e.event_name || e.truck_name || "—"}</div>
                      {e.event_name && <div className="text-xs text-muted-foreground">{e.truck_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div>{e.owner_name}</div>
                      <div className="text-xs">{e.owner_email}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{e.day_label}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtTime(e.starts)} – {fmtTime(e.ends)}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{e.address}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        e.source === "daily" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                      }`}>
                        {e.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(e.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
      </section>

      {/* Admin password-reset result modal. Shows the one-time temp password
          so the admin can copy+share it with the locked-out user out-of-band. */}
      {resetResult && (
        <div
          className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4"
          onClick={() => setResetResult(null)}
          data-testid="reset-password-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-card border border-border p-6 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
                  <Key weight="fill" className="h-3.5 w-3.5" /> Password reset
                </div>
                <h3 className="mt-1 font-display font-bold text-xl">{resetResult.name || resetResult.email}</h3>
                <p className="text-xs text-muted-foreground">{resetResult.email}</p>
              </div>
              <button
                onClick={() => setResetResult(null)}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 rounded-xl border border-dashed border-border bg-muted/40 p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Temporary password</div>
              <div className="mt-1 font-mono text-lg font-semibold tracking-wider select-all break-all" data-testid="reset-temp-password">
                {resetResult.temp_password}
              </div>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              This password is shown only once. Share it with the user over a secure channel and ask
              them to change it after logging in.
            </p>

            <div className="mt-5 flex gap-2">
              <Button onClick={copyTemp} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full flex-1" data-testid="reset-copy-btn">
                <Copy weight="duotone" className="h-4 w-4" />
                Copy password
              </Button>
              <Button variant="outline" onClick={() => setResetResult(null)} className="rounded-full">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
